<template>
  <div>
    <div class="main-form">
      <div class="logo-img">
        <v-img :src="logo" alt="logo"></v-img>
      </div>
      <div class="form-wrapper login-form">
        <div class="top-ui">
          <h1 v-if="!loggedIn">Welcome back to Fluidsdata</h1>
          <h1 v-else>Select your Tenant Data</h1>
          <p v-if="!loggedIn">
            Sign in and unlock the power of your fluids characterization data and AI.
          </p>
        </div>
        <div class="form-info">
          <div class="inputs-wrap" v-if="loggedIn && getTenantData && getTenantData.length > 0">
            <div class="input-info">
              <label>Tenants Data</label>
              <v-select
                v-model="selectedTenant"
                variant="solo"
                class="custom-text-field"
                :items="getTenantData.map((tenant) => tenant.name)"
                placeholder="Please select tenant"
                @update:modelValue="onSelectTenant"
                hide-details
              ></v-select>
            </div>
          </div>
          <v-form v-else ref="form" v-model="form">
            <div class="inputs-wrap">
              <div class="input-info">
                <label>Email Address</label>
                <v-text-field
                  type="email"
                  variant="solo"
                  v-model="email"
                  :rules="[required, emailRule]"
                  placeholder="Email Address here"
                  hide-details
                  @keyup.enter="handleEnterKey"
                   class="custom-text-field"
                ></v-text-field>
              </div>
              <div class="input-info">
                <label>Password</label>
                <v-text-field
                  :type="show ? 'text' : 'password'"
                  hide-details
                   class="custom-text-field"
                  variant="solo"
                  placeholder="Password here"
                  :rules="[required, passwordRule]"
                  v-model="password"
                  @keyup.enter="handleEnterKey"
                >
                  <template #append>
                    <img
                      :src="show ? eyeClosed : eyeOpen"
                      @click="togglePasswordVisibility"
                      alt="Toggle Password Visibility"
                      style="cursor: pointer; width: 24px; height: 24px;"
                    />
                  </template>
                </v-text-field>
              </div>
            </div>
          </v-form>
        </div>
        <div class="login-buttons-container">
          <v-btn
            v-if="!loggedIn"
            class="login-btn"
            :disabled="loading || !isFormValid"
            :loading="loading"
            block
            @click="validateAndLogin"
            :class="{ filled: isFormValid }"
          >Login</v-btn>
          
          <div v-if="!loggedIn" class="or-divider">
            <span>OR</span>
          </div>

          <v-btn
            v-if="!loggedIn"
            class="azure-login-btn"
            block
            @click="loginWithAzure"
            :loading="azureLoading"
          >
            <img :src="microsoftazure" alt="Azure" class="azure-logo" />
            Continue with Microsoft Azure
          </v-btn>
        </div>
      </div>
    </div>
    <v-dialog
      transition="dialog-top-transition"
      width="417"
      v-model="showAlert"
      persistent
    >
      <v-alert title="Previous Session Detected">
        <p class="session-info">
          Would you like to continue from where you left off ?
        </p>
        <div class="alert-btns-div">
          <v-btn class="cancel-btn" flat @click="goToAssets">No</v-btn>
          <v-btn class="save-btn" flat @click="goToLastView">Yes</v-btn>
        </div>
      </v-alert>
    </v-dialog>
    <v-snackbar
      v-model="snackbarMessage"
      color="#E53535"
      timeout="3000"
      v-if="getLoginError"
    >
      Error : {{ getLoginError }}
    </v-snackbar>
    <v-snackbar
      v-model="timeoutMessage"
      color="#E53535"
      timeout="3000"
      v-if="getLoginError"
    >
      <div class="text-center" v-html="getLoginError"></div>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { fetchMetaData } from "../../../utils/cache";
import axios from 'axios';
import { resolveTenantIdFromBrowser } from "../../../utils/tenant";

export default {
  name: "Form",
  data: () => ({
    logo: require("../../../assets/Images/Auth/logo.png"),
    microsoftazure: require("../../../assets/Images/microsoftazure.png"),
    eyeOpen: require("../../../assets/Svg/eye.svg"),
    eyeClosed: require("../../../assets/Svg/eye-slash.svg"),
    show: false,
    form: false,
    email: null,
    password: null,
    loading: false,
    azureLoading: false,
    showModal: false,
    selectedTenant: null,
    snackbarMessage: null,
    timeoutMessage: null,
    showPassword: false,
    showAlert: false,
    showLoader: true,
    loggedIn: false,
  }),
  computed: {
    ...mapGetters("tenantId", ["getTenantData", "getSelectedTenant"]),
    getLoginError() {
      const error = this.$store.getters["auth/getLoginError"];
      if (
        typeof error === "string" &&
        error &&
        error.includes("Request timeout. Try again")
      ) {
        this.timeoutMessage = error;
      } else {
        this.snackbarMessage = error;
      }
      return error;
    },
    getTenantError() {
      const error = this.$store.getters["tenantId/getTenantError"];
      if (error && error.includes("Request timeout. Try again")) {
        return error;
      }
    },
    passwordRule() {
      return (v) => {
        if (!v) return "Password is required";
        if (v.length < 8 || v.length > 15)
          return "Password must be between 8 and 15 characters";
        return true;
      };
    },
    emailRule() {
      return (v) => {
        if (!v) return "Email is required";
        const validEmail = this.validateEmail(v);
        if (!validEmail) return "Invalid email address";
        return true;
      };
    },
    isFormValid() {
      const isEmailValid = this.emailRule(this.email) === true;
      const isPasswordValid = this.passwordRule(this.password) === true;
      const isValid = isEmailValid && isPasswordValid;
      return isValid;
    },
  },
  created() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const username = urlParams.get('username');
    const firstName = urlParams.get('firstName');
    const lastName = urlParams.get('lastName');
    const role = urlParams.get('role');
    const imageUrl = urlParams.get('imageUrl');
    const expires = urlParams.get('expires');

    if (token && username) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('username', username);
      localStorage.setItem('firstName', firstName);
      localStorage.setItem('lastName', lastName);
      localStorage.setItem('role', role);
      localStorage.setItem('imageUrl', imageUrl);
      localStorage.setItem('expires', expires);

      this.loggedIn = true;
      
      this.callTenants();
    }
  },
  methods: {
    ...mapActions("auth", ["sendLoginRequest", "sendLogoutRequest"]),
    ...mapActions("tenantId", ["getUserData"]),
    
    async loginWithAzure() {
      try {
        this.azureLoading = true;
        const response = await axios.post('/login/sso/url');
        
        if (response.data && response.data.data) {
          window.location.href = response.data.data;
        } else {
          console.error('No redirect URL received from SSO endpoint');
          this.snackbarMessage = 'Failed to initiate Azure login';
        }
      } catch (error) {
        console.error('Error initiating Azure login:', error);
        this.snackbarMessage = 'Failed to initiate Azure login';
      } finally {
        this.azureLoading = false;
      }
    },

    togglePasswordVisibility() {
      this.show = !this.show;
    },
    handleEnterKey() {
      const emailValid = this.email && this.emailRule(this.email) === true;
      const passwordValid = this.password && this.passwordRule(this.password) === true;

      if (emailValid && passwordValid) {
        this.validateAndLogin();
      } else {
        console.log("Validation failed: Cannot proceed to login.");
      }
    },
    validateAndLogin() {
      this.loading = true;
      this.login();
    },
    login() {
      if (!this.isFormValid) return;
      this.loading = true;
      this.sendLoginRequest({
        username: this.email,
        password: this.password,
      })
        .then(async (response) => {
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            this.loggedIn = true;
            await this.callTenants();
          }
          else if (response && response.data && response.data.message) {
          console.error(response.data.message);
          }
          else {
            console.error("Error in login");
          }
        })
        .catch((error) => {
          console.log("Error in login:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async callTenants() {
      this.showLoader = true;
      this.$store
        .dispatch("tenantId/fetchTenants")
        .then(async () => {
          var existingTenantId = resolveTenantIdFromBrowser();

            if (this.getTenantData && this.getTenantData.length === 1) {
                const tenantId = this.getTenantData[0].ID;
                const tenantName = this.getTenantData[0].name;

                this.$store.commit("tenantId/SET_SELECTED_TENANT", { name: tenantName, ID: tenantId });

                await fetchMetaData(tenantId);
                if (existingTenantId === tenantId) {
                    var lastView = localStorage.getItem("lastView");
                    if (lastView) {
                        this.showAlert = true;
                    } else {
                        this.$router.push(`/${tenantId}/data-management`);
                    }
                } else {
                    this.$router.push(`/${tenantId}/data-management`);
                }
            } else if (this.getTenantData && this.getTenantData.length > 1) {
              void 0;
            }
        })
        .catch((error) => {
            console.error("Error fetching tenants:", error);
        })
        .finally(() => {
            this.loading = false;
            this.showLoader = false;
        });
},

    logOut() {
      this.sendLogoutRequest()
        .then((response) => {
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            this.$router.push("/login");
          }
          else if (response && response.data && response.data.message) {
          console.error(response.data.message);
          }
          else {
            console.error("Error in logout");
          }
        })
        .catch((error) => {
          console.error("Error in logout:", error);
        });
    },

    async onSelectTenant(selectedValue) {
    const selectedTenant = this.getTenantData.find(
        (tenant) => tenant.name === selectedValue
    );

    if (selectedTenant) {
        const { name, ID } = selectedTenant;
        var existingTenantId = resolveTenantIdFromBrowser();

        this.$store.commit("tenantId/SET_SELECTED_TENANT", { name, ID });

        await fetchMetaData(ID);
        
        if (existingTenantId === ID) {
            this.showModal = false;
            var lastView = localStorage.getItem("lastView");
            if (lastView) {
                this.showAlert = true;
            } else {
                this.$router.push(`/${ID}/data-management`);
            }
        } else {
            localStorage.removeItem("activeMenu");
            this.$router.push(`/${ID}/data-management`);
        }
    }
},

    goToLastView() {
      const lastView = localStorage.getItem("lastView");
      this.showAlert = false;
      const tenantId = this.getSelectedTenant?.ID || resolveTenantIdFromBrowser();
      if (lastView && tenantId) {
        try {
          const pathname = lastView.startsWith("/")
            ? lastView.split("?")[0]
            : `/${lastView.split("?")[0]}`;
          const query = lastView.includes("?") ? lastView.slice(lastView.indexOf("?")) : "";
          const segments = pathname.split("/").filter(Boolean);
          const rest =
            segments.length > 0 && segments[0] === tenantId
              ? segments.slice(1)
              : segments;
          const rebuilt =
            rest.length > 0
              ? `/${tenantId}/${rest.join("/")}${query}`
              : `/${tenantId}/data-management`;
          this.$router.push(rebuilt);
        } catch {
          this.$router.push(`/${tenantId}/data-management`);
        }
      } else if (tenantId) {
        this.$router.push(`/${tenantId}/data-management`);
      }
    },
    goToAssets() {
      const newId = this.getSelectedTenant?.ID;
        if (newId) this.$router.push(`/${newId}/data-management`);
      localStorage.removeItem("activeMenu")
    },

    required(v) {
      return !!v || "Field is required";
    },
    validateEmail(email) {
      const re = /\S+@\S+\.\S+/;
      return re.test(email);
    },
    closeModal() {
      this.showModal = false;
      localStorage.removeItem("authToken");
    },
  },
};
</script>